import React, { useMemo, useState, useEffect, useRef } from 'react';
import './EvolutionTree.css';

/**
 * EvolutionTree — animated SVG tree diagram of the design remix lineage.
 *
 * Props:
 *   chain        — Array of { prompt, imageUrl, styleLabel, isGenerating? }
 *                  Items in chronological order (first = original, last = current/current-in-progress)
 *   onNavigate   — (index) => void  — click a past node to jump back
 *   onClear      — () => void       — clear all remix history
 */

// Layout constants
const NODE_W   = 110;  // node rect width
const NODE_H   = 72;   // node rect height (includes thumb + text)
const THUMB_W  = 46;   // thumbnail width inside node
const THUMB_H  = 46;   // thumbnail height inside node
const H_GAP    = 28;   // horizontal gap between nodes
const V_GAP    = 40;   // vertical gap (unused for now — linear layout only)
const RADIUS   = 8;    // corner radius of node rect
const PAD_Y    = 20;   // top/bottom padding in SVG
const PAD_X    = 12;   // left/right padding in SVG

// Truncate long text
const trunc = (str = '', max = 20) =>
  str.length > max ? str.slice(0, max - 1) + '…' : str;

// Compute SVG layout for a linear chain
function buildLayout(chain) {
  return chain.map((step, i) => ({
    ...step,
    x: PAD_X + i * (NODE_W + H_GAP),
    y: PAD_Y,
    index: i,
  }));
}

// Simple cubic bezier path between two rects
function edgePath(fromNode, toNode) {
  const x1 = fromNode.x + NODE_W;
  const y1 = fromNode.y + NODE_H / 2;
  const x2 = toNode.x;
  const y2 = toNode.y + NODE_H / 2;
  const cpX = (x1 + x2) / 2;
  return `M ${x1} ${y1} C ${cpX} ${y1} ${cpX} ${y2} ${x2} ${y2}`;
}

// Measure SVG stroke-dasharray via getTotalLength approximation
// We use a trick: set dasharray/offset to 1 then animate to 0 for drawn effect
function TreeNode({ node, isLast, onNavigate }) {
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);
  const imgRef = useRef(null);
  const isCurrent = isLast;
  const isOrigin = node.index === 0;
  const label = isOrigin ? 'Original' : (node.styleLabel || `Remix ${node.index}`);

  // foreignObject for real <img> inside SVG
  const imgX = node.x + 4;
  const imgY = node.y + 4;
  const textX = node.x + 4 + THUMB_W + 6;
  const labelY = node.y + 18;
  const promptY = node.y + 30;

  return (
    <g
      className={`evo-node${isCurrent ? ' current' : ''}`}
      onClick={() => !isCurrent && onNavigate?.(node.index)}
      style={{ animationDelay: `${node.index * 60}ms` }}
    >
      {/* Glow ring for current */}
      {isCurrent && (
        <rect
          x={node.x - 3} y={node.y - 3}
          width={NODE_W + 6} height={NODE_H + 6}
          rx={RADIUS + 2}
          className="evo-node-ring"
        />
      )}

      {/* Background */}
      <rect
        x={node.x} y={node.y}
        width={NODE_W} height={NODE_H}
        rx={RADIUS}
        className="evo-node-bg"
      />

      {/* Thumbnail via foreignObject */}
      <foreignObject x={imgX} y={imgY} width={THUMB_W} height={THUMB_H} style={{ borderRadius: 6, overflow: 'hidden' }}>
        {node.isGenerating ? (
          /* Spinner placeholder while generating */
          <div xmlns="http://www.w3.org/1999/xhtml"
            style={{
              width: THUMB_W, height: THUMB_H,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(139,92,246,0.12)', borderRadius: 6,
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="9" stroke="rgba(139,92,246,0.4)" strokeWidth="2" fill="none"/>
              <path d="M12 3 A 9 9 0 0 1 21 12" stroke="#8b5cf6" strokeWidth="2" fill="none" strokeLinecap="round"
                style={{ animation: 'evo-spin 0.9s linear infinite', transformOrigin: '12px 12px' }}
              />
            </svg>
          </div>
        ) : node.imageUrl && !imgError ? (
          <img
            ref={imgRef}
            xmlns="http://www.w3.org/1999/xhtml"
            src={node.imageUrl}
            alt=""
            onLoad={() => setImgLoaded(true)}
            onError={() => setImgError(true)}
            style={{
              width: THUMB_W, height: THUMB_H,
              objectFit: 'cover', borderRadius: 6, display: 'block',
              opacity: imgLoaded ? 1 : 0,
              transition: 'opacity 0.3s',
            }}
          />
        ) : (
          <div xmlns="http://www.w3.org/1999/xhtml"
            style={{
              width: THUMB_W, height: THUMB_H,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(255,255,255,0.06)', borderRadius: 6,
              fontSize: 20,
            }}
          >
            🎨
          </div>
        )}
      </foreignObject>

      {/* Text: label */}
      <text x={textX} y={labelY} className="evo-node-label">
        {trunc(label, 13)}
      </text>

      {/* Text: prompt (2 lines) */}
      <text x={textX} y={promptY} className="evo-node-prompt">
        <tspan x={textX} dy="0">{trunc(node.prompt, 16)}</tspan>
        {node.prompt?.length > 16 && (
          <tspan x={textX} dy="10">{trunc(node.prompt.slice(16), 16)}</tspan>
        )}
      </text>

      {/* Step badge */}
      <circle
        cx={node.x + NODE_W - 9} cy={node.y + 9} r={8}
        fill={isCurrent ? 'rgba(139,92,246,0.6)' : 'rgba(255,255,255,0.08)'}
      />
      <text
        x={node.x + NODE_W - 9} y={node.y + 13}
        textAnchor="middle"
        style={{
          fontSize: 8, fill: 'rgba(255,255,255,0.8)',
          fontFamily: '-apple-system, sans-serif',
          fontWeight: 700,
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      >
        {node.index + 1}
      </text>
    </g>
  );
}

export default function EvolutionTree({ chain = [], onNavigate, onClear, isGenerating = false }) {
  const [collapsed, setCollapsed] = useState(false);

  // If currently generating, append a ghost node for the in-progress step
  const displayChain = useMemo(() => {
    if (isGenerating && chain.length > 0) {
      return [
        ...chain,
        {
          prompt: 'Generating…',
          imageUrl: null,
          styleLabel: null,
          isGenerating: true,
        },
      ];
    }
    return chain;
  }, [chain, isGenerating]);

  const nodes = useMemo(() => buildLayout(displayChain), [displayChain]);

  // Need at least 2 nodes (original + at least 1 remix) to show the tree
  if (!chain || chain.length < 2) return null;

  const svgW = PAD_X + nodes.length * (NODE_W + H_GAP) - H_GAP + PAD_X;
  const svgH = PAD_Y + NODE_H + PAD_Y;

  return (
    <div className="evo-tree-wrap">
      <div className="evo-tree-header">
        <span>🌳</span>
        <span className="evo-tree-title">Evolution Tree</span>
        <span className="evo-tree-count">{chain.length} steps</span>
        <button
          className="evo-tree-toggle"
          onClick={() => setCollapsed(c => !c)}
          aria-label={collapsed ? 'Expand evolution tree' : 'Collapse evolution tree'}
        >
          {collapsed ? '▼ Show' : '▲ Hide'}
        </button>
        {onClear && (
          <button
            className="evo-tree-toggle"
            onClick={onClear}
            title="Clear evolution history"
            style={{ marginLeft: 4 }}
          >
            ✕ Clear
          </button>
        )}
      </div>

      {!collapsed && (
        <div className="evo-tree-scroll">
          <svg
            className="evo-tree-svg"
            width={svgW}
            height={svgH}
            viewBox={`0 0 ${svgW} ${svgH}`}
          >
            <defs>
              {/* Arrow marker */}
              <marker
                id="evo-arrow"
                markerWidth="6" markerHeight="6"
                refX="5" refY="3"
                orient="auto"
              >
                <path d="M0,0 L6,3 L0,6 Z" fill="rgba(139,92,246,0.5)" />
              </marker>
            </defs>

            {/* Edges */}
            {nodes.slice(0, -1).map((node, i) => {
              const path = edgePath(node, nodes[i + 1]);
              return (
                <path
                  key={`edge-${i}`}
                  className="evo-edge"
                  d={path}
                  markerEnd="url(#evo-arrow)"
                  style={{
                    pathLength: 1,
                    strokeDasharray: 1,
                    strokeDashoffset: 1,
                    animationDelay: `${i * 80}ms`,
                  }}
                />
              );
            })}

            {/* Nodes */}
            {nodes.map((node, i) => (
              <TreeNode
                key={`node-${i}`}
                node={node}
                isLast={i === nodes.length - 1 && !isGenerating}
                onNavigate={onNavigate}
              />
            ))}
          </svg>
        </div>
      )}
    </div>
  );
}
